import { formatLargeNumber } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';
import { isSuccessStatus } from 'components/admin/pages/Imports/ImportRequests/components/ProcessImportSteps/lib/constants';

// Sent to the initiator's own room only (never the practice room — see
// utility/astro/importSocket.js), so anything arriving here is this user's own
// import and needs no "is it mine?" check. Both ledgers report here: a mapped
// CSV sends its per-record `summary`, an EHR run its `counts`.
//
// The toast is the one notification that is always sent: an import takes minutes
// and the operator has moved on to another screen by the time it lands. The
// emailed report is opt-in on top of this.

// What the operator needs from a glance: did it land, and is there anything to
// do about it. The detail is on the request's Review step.
const summaryLine = (summary) => {
  const imported = (summary.success || 0) + (summary.info || 0) + (summary.partial || 0);
  const pending =
    (summary.info || 0) +
    (summary.partial || 0) +
    (summary.duplicates || 0) +
    (summary.errors || 0);

  const landed = `${formatLargeNumber(imported)} ${imported === 1 ? 'record' : 'records'} imported`;
  if (!pending) return landed;
  return `${landed} · ${formatLargeNumber(pending)} to review`;
};

// An EHR run has no per-record resolutions, so it reports what it wrote and what
// it could not read.
const countsLine = (counts) => {
  const documents = counts.documents || 0;
  const landed = `${formatLargeNumber(documents)} ${
    documents === 1 ? 'document' : 'documents'
  } imported`;
  const errors = counts.errors || 0;
  const skipped = counts.skipped || 0;
  if (!errors && !skipped) return landed;
  return `${landed} · ${formatLargeNumber(errors)} errors · ${formatLargeNumber(skipped)} skipped`;
};

export default (data) => {
  const { id, status, fileName, practiceName, summary, counts } = data || {};

  const failed = !isSuccessStatus(status);

  // A CSV run names its file; an EHR run sends neither a file name nor a
  // summary, so it is named by its ledger number and reports its counts.
  const subject = [fileName || (id ? `EHR import #${id}` : null), practiceName]
    .filter(Boolean)
    .join(' · ');

  const detail = summary ? summaryLine(summary) : counts ? countsLine(counts) : null;

  showAlert({
    color: failed ? 'danger' : 'success',
    title: failed ? 'Import failed' : 'Import finished',
    message: [subject, failed ? null : detail].filter(Boolean).join(' — ')
  });
};
